import { supabase } from '../utils/supabase';

/**
 * Fetches hired VAs for a specific employer
 * @param {string} employerId - The employer's user ID
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getHiredVAs = async (employerId) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[getHiredVAs] Starting fetch for employer:', employerId);
  }
  
  try {
    // Simplified approach: fetch contracts first, then related data separately
    const { data: contractsData, error: contractsError } = await supabase
      .from('contracts')
      .select('*')
      .eq('employer_id', employerId)
      .order('start_date', { ascending: false });

    if (process.env.NODE_ENV === 'development') {
      console.log('[getHiredVAs] Contracts query result:', { contractsData, contractsError });
    }

    if (contractsError) {
      console.warn('Error fetching from contracts:', contractsError);
    }

    // Also fetch from interview_schedules with hiring_status = 'hired'
    if (process.env.NODE_ENV === 'development') {
      console.log('[getHiredVAs] Fetching interview schedules for employer:', employerId);
    }
    
    const { data: interviewData, error: interviewError } = await supabase
      .from('interview_schedules')
      .select(`
        id,
        decision_date,
        hiring_status,
        offer_amount,
        hiring_notes,
        candidate_id,
        application_id,
        feedback,
        job_applications!inner (
          id,
          job_id,
          status,
          applied_at,
          jobs!inner (
            id,
            title,
            employer_id
          )
        ),
        candidate:user_profiles!candidate_id (
          id,
          full_name,
          email,
          profile_picture,
          skills,
          location,
          bio,
          hourly_rate,
          experience_years,
          phone,
          rating,
          total_reviews
        )
      `)
      .eq('job_applications.jobs.employer_id', employerId)
      .eq('hiring_status', 'hired')
      .order('decision_date', { ascending: false });

    if (process.env.NODE_ENV === 'development') {
      console.log('[getHiredVAs] Interview query result:', { interviewData, interviewError });
    }

    if (interviewError) {
      console.warn('Error fetching from interview_schedules:', interviewError);
    }

    // Combine both data sources
    let combinedData = [];

    // Process contracts data with separate queries for related data
    if (contractsData && contractsData.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[getHiredVAs] Processing', contractsData.length, 'contracts');
      }
      
      // Get all VA IDs from contracts
      const vaIds = contractsData.map(c => c.va_id).filter(Boolean);
      const jobIds = contractsData.map(c => c.job_id).filter(Boolean);
      const contractIds = contractsData.map(c => c.id);
      
      // Fetch VA profiles
      let vaProfiles = [];
      if (vaIds.length > 0) {
        const { data: vaData, error: vaError } = await supabase
          .from('user_profiles')
          .select('*')
          .in('id', vaIds);
          
        if (vaError) {
          console.warn('Error fetching VA profiles:', vaError);
        } else {
          vaProfiles = vaData || [];
        }
      }
      
      // Fetch job details
      let jobs = [];
      if (jobIds.length > 0) {
        const { data: jobData, error: jobError } = await supabase
          .from('jobs')
          .select('id, title')
          .in('id', jobIds);
          
        if (jobError) {
          console.warn('Error fetching jobs:', jobError);
        } else {
          jobs = jobData || [];
        }
      }
      
      // Fetch projects
      let projects = [];
      if (contractIds.length > 0) {
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('*')
          .in('contract_id', contractIds);
          
        if (projectError) {
          console.warn('Error fetching projects:', projectError);
        } else {
          projects = projectData || [];
        }
      }
      
      // Fetch performance metrics
      let performanceMetrics = [];
      if (contractIds.length > 0) {
        const { data: metricsData, error: metricsError } = await supabase
          .from('performance_metrics')
          .select('*')
          .in('contract_id', contractIds);
          
        if (metricsError) {
          console.warn('Error fetching performance metrics:', metricsError);
        } else {
          performanceMetrics = metricsData || [];
        }
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[getHiredVAs] Related data fetched:', {
          vaProfiles: vaProfiles.length,
          jobs: jobs.length,
          projects: projects.length,
          performanceMetrics: performanceMetrics.length
        });
      }

      const processedContractsData = contractsData.map(contract => {
        // Find related data
        const va = vaProfiles.find(v => v.id === contract.va_id);
        const job = jobs.find(j => j.id === contract.job_id);
        const project = projects.find(p => p.contract_id === contract.id);
        const metrics = performanceMetrics.find(m => m.contract_id === contract.id);
        
        return {
          // Contract info
          id: contract.id,
          contract_id: contract.id,
          va_id: contract.va_id,
          job_id: contract.job_id,
          status: contract.status,
          hourly_rate: contract.hourly_rate,
          start_date: contract.start_date,
          end_date: contract.end_date,
          
          // VA info from user_profiles
          full_name: va?.full_name || 'Unknown VA',
          email: va?.email,
          profile_picture: va?.profile_picture,
          location: va?.location,
          skills: va?.skills,
          bio: va?.bio,
          experience_years: va?.experience_years,
          phone: va?.phone,
          
          // Job info
          title: job?.title || 'Virtual Assistant',
          
          // Project info if available
          project_id: project?.id,
          project_name: project?.name,
          project_description: project?.description,
          project_due_date: project?.due_date,
          project_progress: project?.progress,
          
          // Performance metrics if available
          rating: metrics?.rating || va?.rating || 0,
          tasks_completed: metrics?.tasks_completed || 0,
          hours_worked: metrics?.hours_worked || 0,
          total_paid: metrics?.total_paid || 0,
          total_reviews: va?.total_reviews || 0,

          // Meta
          hired_at: contract.created_at,
          last_activity: contract.updated_at,

          // Source
          source: 'contracts'
        };
      });

      combinedData = [...processedContractsData];
      if (process.env.NODE_ENV === 'development') {
        console.log('[getHiredVAs] Processed contracts data:', combinedData.length, 'items');
      }
    }

    // Process interview data
    if (interviewData && interviewData.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[getHiredVAs] Processing', interviewData.length, 'interview schedules');
      }
      
      const processedInterviewData = interviewData
        .filter(interview => {
          // Filter out interviews with missing critical data
          if (!interview.candidate || !interview.job_applications) {
            console.warn('[getHiredVAs] Skipping interview with missing candidate or application data:', {
              interview_id: interview.id,
              has_candidate: !!interview.candidate,
              has_application: !!interview.job_applications
            });
            return false;
          }
          return true;
        })
        .map(interview => {
          const va = interview.candidate;
          const application = interview.job_applications;
          
          return {
            id: interview.id,
            contract_id: null, // No contract yet
            va_id: va?.id,
            job_id: application?.job_id,
            application_id: interview.application_id,
            status: 'pending', // Default status for newly hired VAs
            hourly_rate: interview.offer_amount || va?.hourly_rate || 0,
            start_date: interview.decision_date,
            end_date: null,
            
            // VA info
            full_name: va?.full_name || 'Unknown VA',
            email: va?.email,
            profile_picture: va?.profile_picture,
            location: va?.location,
            skills: va?.skills,
            bio: va?.bio,
            experience_years: va?.experience_years,
            phone: va?.phone,
            
            // Job info
            title: application?.jobs?.title || 'Virtual Assistant',
            
            // Project placeholders
            project_id: null,
            project_name: null,
            project_description: null,
            project_due_date: null,
            project_progress: 0,
            
            // Performance metrics
            rating: va?.rating || 0,
            tasks_completed: 0,
            hours_worked: 0,
            total_paid: 0,
            total_reviews: va?.total_reviews || 0,
            
            // Meta
            hired_at: interview.decision_date,
            hiring_notes: interview.hiring_notes,
            last_activity: interview.updated_at,
            
            // Interview feedback
            interview_feedback: interview.feedback,
            
            // Source
            source: 'interviews'
          };
        });

      // Add interview data to combined data
      // But first filter out any VAs that are already in contracts
      const existingVaJobCombos = new Set(
        combinedData.map(item => `${item.va_id}-${item.job_id}`)
      );
      const uniqueInterviewData = processedInterviewData.filter(
        item => !existingVaJobCombos.has(`${item.va_id}-${item.job_id}`)
      );
      
      combinedData = [...combinedData, ...uniqueInterviewData];
    }

    // If both queries failed, throw an error
    if (contractsError && interviewError) {
      throw new Error('Failed to fetch hired VAs from both contracts and interviews');
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[getHiredVAs] Final result:', {
        totalItems: combinedData.length,
        contractItems: combinedData.filter(item => item.source === 'contracts').length,
        interviewItems: combinedData.filter(item => item.source === 'interviews').length
      });
    }

    return { success: true, data: combinedData };
  } catch (err) {
    console.error('[vaService] getHiredVAs error:', err);
    return { 
      success: false, 
      error: err.message || 'Failed to fetch hired VAs'
    };
  }
};

/**
 * Fetches hired VAs for a specific employer
 * @param {string} employerId - The employer's user ID
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getHiredVAsByEmployerId = async (employerId) => {
  try {
    // Fetch hired VAs using the contracts table
    const { data, error } = await supabase
      .from('contracts')
      .select(`
        id,
        start_date,
        status,
        hourly_rate,
        va_id,
        user_profiles!va_id (
          id,
          full_name,
          profile_picture,
          location,
          skills,
          bio,
          title
        ),
        projects (
          id,
          name,
          description,
          due_date,
          progress
        ),
        performance_metrics (
          rating,
          tasks_completed,
          hours_worked,
          total_paid
        )
      `)
      .eq('employer_id', employerId)
      .order('start_date', { ascending: false });

    if (error) throw error;

    // Transform the data to match the component's expected format
    const formattedVAs = data.map(contract => ({
      id: contract.va_id,
      contractId: contract.id,
      name: contract.user_profiles?.full_name || 'Unknown',
      role: contract.user_profiles?.title 
        || (typeof contract.user_profiles?.bio === 'string' 
            ? contract.user_profiles.bio.substring(0, 40) + '...' 
            : undefined)
        || 'Virtual Assistant',
      avatar: contract.user_profiles?.profile_picture || '/assets/default-avatar.png',
      status: contract.status,
      startDate: new Date(contract.start_date).toLocaleDateString(),
      location: contract.user_profiles?.location || 'Remote',
      skills: Array.isArray(contract.user_profiles?.skills) 
        ? contract.user_profiles?.skills 
        : (typeof contract.user_profiles?.skills === 'object' 
          ? Object.values(contract.user_profiles?.skills) 
          : []),
      hourlyRate: contract.hourly_rate,
      performance: {
        rating: contract.performance_metrics?.length > 0 
          ? parseFloat(contract.performance_metrics[0]?.rating) || 0 
          : 0,
        tasksCompleted: contract.performance_metrics?.length > 0 
          ? contract.performance_metrics[0]?.tasks_completed || 0 
          : 0,
        hoursWorked: contract.performance_metrics?.length > 0 
          ? contract.performance_metrics[0]?.hours_worked || 0 
          : 0,
        totalPaid: contract.performance_metrics?.length > 0 
          ? parseFloat(contract.performance_metrics[0]?.total_paid) || 0 
          : 0
      },
      currentProject: contract.projects?.length > 0 ? {
        id: contract.projects[0].id,
        name: contract.projects[0].name,
        description: contract.projects[0].description,
        progress: contract.projects[0].progress,
        dueDate: contract.projects[0].due_date 
          ? new Date(contract.projects[0].due_date).toLocaleDateString() 
          : 'No deadline'
      } : null
    }));

    return { success: true, data: formattedVAs };
  } catch (err) {
    console.error('[vaService] getHiredVAsByEmployerId:', err);
    return { 
      success: false, 
      error: err.message || 'Failed to fetch hired VAs'
    };
  }
};

/**
 * Ends a contract between an employer and VA
 * @param {string} contractId - The contract ID
 * @param {string} endReason - The reason for ending the contract
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const endContract = async (contractId, endReason) => {
  try {
    const { data, error } = await supabase
      .from('contracts')
      .update({ 
        status: 'completed', 
        end_date: new Date().toISOString(),
        end_reason: endReason
      })
      .eq('id', contractId);
      
    if (error) throw error;
    return { success: true, data };
  } catch (err) {
    console.error('[vaService] endContract:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Submits a review for a VA
 * @param {Object} reviewData - The review data
 * @param {string} reviewData.contractId - The contract ID
 * @param {string} reviewData.employerId - The employer ID
 * @param {string} reviewData.vaId - The VA ID
 * @param {number} reviewData.rating - Rating from 1-5
 * @param {string} reviewData.content - Review text
 * @param {boolean} reviewData.isPublic - Whether review is public
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const submitReview = async (reviewData) => {
  try {
    // Insert the review
    const { data, error } = await supabase
      .from('reviews')
      .insert([
        {
          contract_id: reviewData.contractId,
          employer_id: reviewData.employerId,
          va_id: reviewData.vaId,
          rating: reviewData.rating,
          content: reviewData.content,
          is_public: reviewData.isPublic
        }
      ]);

    if (error) throw error;
    
    // Update the performance metrics
    const { error: metricsError } = await supabase
      .from('performance_metrics')
      .update({ rating: reviewData.rating })
      .eq('contract_id', reviewData.contractId);
    
    if (metricsError) throw metricsError;
    
    return { success: true, data };
  } catch (err) {
    console.error('[vaService] submitReview:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Updates the VA's work status (active, on-break, etc.)
 * @param {string} contractId - The contract ID
 * @param {string} status - The new status
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const updateVAStatus = async (contractId, status) => {
  try {
    const { data, error } = await supabase
      .from('contracts')
      .update({ status })
      .eq('id', contractId);
      
    if (error) throw error;
    return { success: true, data };
  } catch (err) {
    console.error('[vaService] updateVAStatus:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Creates a new project for a VA
 * @param {Object} projectData - The project data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const createProject = async (projectData) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .insert([projectData]);
      
    if (error) throw error;
    return { success: true, data };
  } catch (err) {
    console.error('[vaService] createProject:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Updates a project for a VA
 * @param {string} projectId - The project ID
 * @param {Object} projectData - The updated project data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const updateProject = async (projectId, projectData) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update(projectData)
      .eq('id', projectId);
      
    if (error) throw error;
    return { success: true, data };
  } catch (err) {
    console.error('[vaService] updateProject:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Updates performance metrics for a VA
 * @param {string} contractId - The contract ID
 * @param {Object} metricsData - The updated metrics data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const updatePerformanceMetrics = async (contractId, metricsData) => {
  try {
    // First check if metrics exist for this contract
    const { data: existingMetrics, error: checkError } = await supabase
      .from('performance_metrics')
      .select('id')
      .eq('contract_id', contractId)
      .single();
    
    if (checkError && checkError.code !== 'PGRST116') throw checkError;
    
    let result;
    
    if (existingMetrics) {
      // Update existing metrics
      const { data, error } = await supabase
        .from('performance_metrics')
        .update(metricsData)
        .eq('contract_id', contractId);
        
      if (error) throw error;
      result = data;
    } else {
      // Insert new metrics
      const { data, error } = await supabase
        .from('performance_metrics')
        .insert([{
          contract_id: contractId,
          ...metricsData
        }]);
        
      if (error) throw error;
      result = data;
    }
    
    return { success: true, data: result };
  } catch (err) {
    console.error('[vaService] updatePerformanceMetrics:', err);
    return { success: false, error: err.message };
  }
};
